import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface UserAccessAPI {
  id: string;
  values: {
    entity: number;
    databaseId?: string;
  };
  licenseId: string;
  admin?: boolean;
  name?: string;
  context?: string;
  system?: number;
}

interface EntityInfoAPI {
  id: number;
  name: string;
}

interface ServidorAPI {
  id: number;
  pessoa: {
    nome: string;
  };
}

interface AnoLetivoAPI {
  id: number;
  ano: string | number;
  descricao?: string;
}

interface EstabelecimentoAPI {
  id: number;
  descricao: string;
}

interface TurmaAPI {
  id: number;
  descricao: string;
  nome?: string;
  tipoAtendimento?: string;
  etapaMatriz?: {
    modular?: boolean;
  };
  estabelecimento?: {
    id: number;
    nome: string;
  };
}

interface ComponenteAPI {
  id: number;
  descricao: string;
  nome?: string;
  isCampoExperiencia?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ContextApiService {
  private apiUrl = environment.apiUrl;
  private get userAccountsUrl(): string {
    const hostname = window.location.hostname;
    const base = (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('test.betha.cloud'))
      ? 'https://plataforma-autorizacoes.test.betha.cloud'
      : 'https://plataforma-autorizacoes.betha.cloud';
    return `${base}/user-accounts/v0.1`;
  }

  constructor(private http: HttpClient) {}

  getMyAccess(): Observable<UserAccessAPI[]> {
    return this.http.get<UserAccessAPI[]>(`${this.userAccountsUrl}/api/suite/users/@me/access`).pipe(
      map(acessos => {
        // Remove acessos sem databaseId/licenseId e filtra pelo sistema atual (Professores = 113)
        const acessosFiltrados = acessos.filter(acesso => {
          const validAccess = !acesso.values.databaseId && acesso.licenseId;
          const validSystem = environment.systemId ? (Number(acesso.system) === environment.systemId) : true;
          return validAccess && validSystem;
        });
        return acessosFiltrados;
      })
    );
  }

  getEntitiesInfo(ids: number[]): Observable<EntityInfoAPI[]> {
    const params = ids.map(id => `id=${id}`).join('&');
    const url = `${this.userAccountsUrl}/api/entities?${params}`;
    return this.http.get<EntityInfoAPI[]>(url).pipe(
      map(infos => {
        return infos;
      })
    );
  }

  /** Um usuário pode ter múltiplos acessos pra mesma entidade, cada um com licenseId diferente. */
  validateAccess(accessId: string): Observable<UserAccessAPI> {
    const url = `${this.userAccountsUrl}/api/suite/users/@me/access/${accessId}`;

    return this.http.get<UserAccessAPI>(url).pipe(
      map(access => {
        return access;
      })
    );
  }

  /**
   * NÃO agrupa por entidade: cada acesso é uma opção separada (mesmo nome pode repetir),
   * porque cada um tem um licenseId diferente e específico.
   */
  listEntidades(): Observable<Array<{ id: number; nome: string; licenseId: string; access: string; admin?: boolean }>> {
    return this.getMyAccess().pipe(
      switchMap(acessos => {
        const entityIds = [...new Set(acessos.map(a => a.values.entity))];

        return this.getEntitiesInfo(entityIds).pipe(
          map(infos => {
            const todosAcessos = acessos.map((acesso, index) => {
              const entityId = Number(acesso.values.entity);
              const info = infos.find(i => i.id === entityId);

              const acessoMapeado = {
                id: acesso.values.entity,
                nome: info?.name || 'Entidade sem nome',
                licenseId: acesso.licenseId,
                access: acesso.id,
                admin: Boolean((acesso as any).admin ?? (acesso as any).values?.admin)
              };

              return acessoMapeado;
            });

            return todosAcessos;
          })
        );
      })
    );
  }

  getServidores(params?: { filter?: string; offset?: number; limit?: number; userAccessId?: string }): Observable<ServidorAPI[]> {
    const termoBruto = params?.filter?.trim() ?? '';
    const filterRsql = termoBruto
      ? `(nome like "${encodeURIComponent('%' + termoBruto + '%')}")` 
      : '';

    const defaultParams = {
      offset: 0,
      limit: 20,
      ...params,
      filter: filterRsql
    };

    let httpParams = new HttpParams();
    Object.keys(defaultParams).forEach(key => {
      if (key !== 'userAccessId') {
        httpParams = httpParams.set(key, String(defaultParams[key as keyof typeof defaultParams]));
      }
    });
    
    const headers: { [key: string]: string } = {};
    if (params?.userAccessId) {
      headers['User-Access'] = params.userAccessId;
      headers['X-Skip-App-Context'] = 'true';
    }
    
    return this.http.get<{ content: ServidorAPI[]; hasNext: boolean }>(`${this.apiUrl}/context/servidor`, { params: httpParams, headers }).pipe(
      map(response => {
        return response.content || [];
      })
    );
  }

  getServidor(id: number): Observable<ServidorAPI> {
    return this.http.get<ServidorAPI>(`${this.apiUrl}/professores/servidor/filtro-gestor/${id}`);
  }

  /** Retorna null se o usuário for secretário (sem servidor vinculado), não professor. */
  getServidorDefaultProfessor(entityId: number, licenseId: string): Observable<ServidorAPI | null> {
    return this.http.get<ServidorAPI>(
      `${this.apiUrl}/professores/servidor/entidade/${entityId}`,
      {
        headers: {
          'User-Access': licenseId,
          'X-Skip-App-Context': 'true'  // Impede o interceptor de injetar app-context stale de sessão anterior
        }
      }
    ).pipe(
      catchError(() => of(null))
    );
  }

  // X-Skip-App-Context: evita que o interceptor injete app-context antigo (stale) quando
  // um app-context novo é montado aqui a partir dos parâmetros de contexto selecionados.
  getAnosLetivos(params?: { userAccessId?: string; servidorId?: number }): Observable<AnoLetivoAPI[]> {
    const headers: { [key: string]: string } = {};

    if (params?.userAccessId) {
      headers['User-Access'] = params.userAccessId;
      headers['X-Skip-App-Context'] = 'true';
    }

    if (params?.servidorId) {
      const appContext = JSON.stringify({ servidor: params.servidorId });
      headers['app-context'] = btoa(appContext);
    }

    return this.http.get<AnoLetivoAPI[]>(`${this.apiUrl}/context/ano-letivo`, { headers });
  }

  getAnoLetivo(ano: string | number): Observable<AnoLetivoAPI> {
    return this.http.get<AnoLetivoAPI>(`${this.apiUrl}/context/ano-letivo/ano/${ano}`);
  }

  getEstabelecimentos(params?: { userAccessId?: string; servidorId?: number; anoLetivoId?: number }): Observable<EstabelecimentoAPI[]> {
    const headers: { [key: string]: string } = {};

    if (params?.userAccessId) {
      headers['User-Access'] = params.userAccessId;
      headers['X-Skip-App-Context'] = 'true';
    }

    if (params?.servidorId || params?.anoLetivoId) {
      const appContextObj: any = {};
      if (params.servidorId) {
        appContextObj.servidor = params.servidorId;
      }
      if (params.anoLetivoId) {
        appContextObj.anoletivo = { value: params.anoLetivoId, insulation: false };
      }
      const appContext = JSON.stringify(appContextObj);
      headers['app-context'] = btoa(appContext);
    }

    return this.http.get<EstabelecimentoAPI[]>(`${this.apiUrl}/context/estabelecimento`, { headers });
  }

  getEstabelecimento(id: number): Observable<EstabelecimentoAPI> {
    return this.http.get<EstabelecimentoAPI[]>(`${this.apiUrl}/context/estabelecimento`, {
      params: { id: id.toString() }
    }).pipe(
      map(response => {
        if (response && response.length > 0) {
          return response[0];
        }
        throw new Error('Estabelecimento não encontrado');
      })
    );
  }

  getTurmas(params?: { filter?: string; offset?: number; limit?: number; userAccessId?: string; servidorId?: number; anoLetivoId?: number; estabelecimentoId?: number }): Observable<{ content: TurmaAPI[]; hasNext: boolean }> {
    const defaultParams = {
      filter: '',
      offset: 0,
      limit: 50
    };

    let httpParams = new HttpParams();
    if (params?.filter !== undefined) httpParams = httpParams.set('filter', params.filter);
    if (params?.offset !== undefined) httpParams = httpParams.set('offset', String(params.offset));
    if (params?.limit !== undefined) httpParams = httpParams.set('limit', String(params.limit));

    if (!params?.filter && !params?.offset && !params?.limit) {
      httpParams = httpParams.set('filter', defaultParams.filter);
      httpParams = httpParams.set('offset', String(defaultParams.offset));
      httpParams = httpParams.set('limit', String(defaultParams.limit));
    }
    
    const headers: { [key: string]: string } = {};
    
    if (params?.userAccessId) {
      headers['User-Access'] = params.userAccessId;
      headers['X-Skip-App-Context'] = 'true';
    }

    if (params?.servidorId || params?.anoLetivoId || params?.estabelecimentoId) {
      const appContextObj: any = {};
      if (params.servidorId) {
        appContextObj.servidor = params.servidorId;
      }
      if (params.anoLetivoId) {
        appContextObj.anoletivo = { value: params.anoLetivoId, insulation: false };
      }
      if (params.estabelecimentoId) {
        appContextObj.estabelecimento = { value: String(params.estabelecimentoId), insulation: false };
      }
      const appContext = JSON.stringify(appContextObj);
      headers['app-context'] = btoa(appContext);
    }

    return this.http.get<{ content: TurmaAPI[]; hasNext: boolean }>(`${this.apiUrl}/context/turma`, { params: httpParams, headers });
  }

  getTurma(id: number): Observable<TurmaAPI> {
    const params = new HttpParams()
      .set('filter', `id=${id}`)
      .set('offset', '0')
      .set('limit', '1');

    return this.http.get<{ content: TurmaAPI[] }>(`${this.apiUrl}/context/turma`, { params }).pipe(
      map(response => {
        if (response.content && response.content.length > 0) {
          return response.content[0];
        }
        throw new Error('Turma não encontrada');
      })
    );
  }

  /** A turma vai só na URL — o app-context leva servidor/anoletivo/estabelecimento, igual ao AngularJS. */
  getComponentesCurriculares(
    idTurma: number,
    params?: { userAccessId?: string; servidorId?: number; anoLetivoId?: number; estabelecimentoId?: number }
  ): Observable<ComponenteAPI[]> {
    const headers: { [key: string]: string } = {};

    if (params?.userAccessId) {
      headers['User-Access'] = params.userAccessId;
    }

    if (params?.servidorId || params?.anoLetivoId || params?.estabelecimentoId) {
      const appContextObj: any = {};
      if (params.servidorId) {
        appContextObj.servidor = params.servidorId;
      }
      if (params.anoLetivoId) {
        appContextObj.anoletivo = { value: params.anoLetivoId, insulation: false };
      }
      if (params.estabelecimentoId) {
        appContextObj.estabelecimento = { value: String(params.estabelecimentoId), insulation: false };
      }
      const appContext = JSON.stringify(appContextObj);
      headers['app-context'] = btoa(appContext);
    }
    
    return this.http.get<ComponenteAPI[]>(`${this.apiUrl}/context/turma/${idTurma}/itens-educacionais`, { headers });
  }
}
